import { reactive } from 'vue'
import router from '@/config/index'

const STORAGE_KEY = 'mate_cms_admin_tabs'

const HOME_TAB = {
  path: '/',
  fullPath: '/',
  name: 'dashboard',
  title: '工作台',
  closable: false,
}

function loadPersisted() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    if (!Array.isArray(data?.tabs) || !data.tabs.length) return null
    return data
  } catch {
    return null
  }
}

function createInitialState() {
  const persisted = loadPersisted()
  if (persisted) {
    const tabs = persisted.tabs.map((tab) => ({
      ...tab,
      closable: tab.path !== '/',
    }))
    if (!tabs.some((t) => t.path === '/')) {
      tabs.unshift({ ...HOME_TAB })
    }
    return {
      tabs,
      activeKey: persisted.activeKey || '/',
      cachedNames: tabs.map((t) => t.name).filter(Boolean),
    }
  }
  return {
    tabs: [{ ...HOME_TAB }],
    activeKey: '/',
    cachedNames: [HOME_TAB.name],
  }
}

const state = reactive(createInitialState())

function persist() {
  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      tabs: state.tabs,
      activeKey: state.activeKey,
    })
  )
}

function addTab(route) {
  if (!route?.name || route.meta?.hideTab) return
  if (route.path?.startsWith('/login')) return

  const title = route.meta?.title || String(route.name)
  const existing = state.tabs.find((t) => t.path === route.path)

  if (existing) {
    existing.fullPath = route.fullPath
    existing.title = title
    existing.name = route.name
  } else {
    state.tabs.push({
      path: route.path,
      fullPath: route.fullPath,
      name: route.name,
      title,
      closable: route.path !== '/',
    })
  }

  if (route.name && !state.cachedNames.includes(route.name)) {
    state.cachedNames.push(route.name)
  }

  state.activeKey = route.path
  persist()
}

function removeFromCache(name) {
  if (!name) return
  const idx = state.cachedNames.indexOf(name)
  if (idx > -1) state.cachedNames.splice(idx, 1)
}

function removeTab(path) {
  const idx = state.tabs.findIndex((t) => t.path === path)
  if (idx === -1) return
  const tab = state.tabs[idx]
  if (!tab.closable) return

  state.tabs.splice(idx, 1)
  removeFromCache(tab.name)

  if (state.activeKey === path) {
    const next = state.tabs[idx] || state.tabs[idx - 1] || state.tabs[0]
    state.activeKey = next.path
    persist()
    router.push(next.fullPath)
    return
  }
  persist()
}

function removeOtherTabs(path) {
  const keep = state.tabs.filter((t) => t.path === path || !t.closable)
  const removed = state.tabs.filter((t) => t.path !== path && t.closable)
  removed.forEach((t) => removeFromCache(t.name))
  state.tabs.splice(0, state.tabs.length, ...keep)
  state.activeKey = path
  persist()
  const active = state.tabs.find((t) => t.path === path)
  if (active) router.push(active.fullPath)
}

function removeAllTabs() {
  const home = state.tabs.find((t) => t.path === '/') || { ...HOME_TAB }
  state.tabs.filter((t) => t.closable).forEach((t) => removeFromCache(t.name))
  state.tabs.splice(0, state.tabs.length, { ...home, closable: false })
  state.cachedNames.splice(0, state.cachedNames.length, home.name)
  state.activeKey = '/'
  persist()
  router.push(home.fullPath || '/')
}

function setActiveByRoute(route) {
  if (!route?.path) return
  state.activeKey = route.path
  persist()
}

export const tabsStore = {
  state,
  addTab,
  removeTab,
  removeOtherTabs,
  removeAllTabs,
  setActiveByRoute,
}
